import { type FC } from "react";
import { Navigate, generatePath, useParams } from "react-router-dom";

import { useFragment } from "@apollo/client";

import { AbsoluteRoute, type RouteParams } from "../../core/routes";
import { CofConfigId, type LensSpace, type MyLensesOrganization } from "../../gql-generated/types";
import { PageContentSpinner } from "../../layout/components";
import { lensSpace, organization } from "../../shared/api/fragments";
import { useSelectedLensFolder, useSelectedLensFolderV3 } from "../../shared/hooks";
import { useLens } from "../hooks";

import { LensSourcePage } from "./lens-source";

import { useFeatureGates } from "src/app/cof-feature-gates/hooks";

export const LensPage: FC = () => {
    const { lensId = "" } = useParams<RouteParams[AbsoluteRoute.LENS]>();

    const { loading, data, error } = useLens(lensId, !lensId, true);

    const selectedLensFolderV3 = useSelectedLensFolderV3();

    const { data: lensFolderDataV1, complete: lensFolderDataCompleteV1 } = useFragment<LensSpace>({
        fragment: lensSpace,
        fragmentName: "LensSpaceAll",
        from: {
            __typename: "LensSpace",
            id: data?.getLens.lens?.lensAccountId?.lensSpaceId,
        },
    });

    const { data: orgDataV1, complete: orgDataCompleteV1 } = useFragment<MyLensesOrganization>({
        fragment: organization.all,
        fragmentName: "OrganizationAll",
        from: {
            __typename: "MyLensesOrganization",
            id: lensFolderDataCompleteV1 ? lensFolderDataV1.organizationId : "",
        },
    });

    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const { lensFolder: selectedLensFolderV2 } = useSelectedLensFolder();

    const selectedLensFolder = isMyLensesOrgsV2Enabled ? selectedLensFolderV2 : selectedLensFolderV3;

    const orgData = isMyLensesOrgsV2Enabled ? undefined : orgDataV1;
    const orgDataComplete = isMyLensesOrgsV2Enabled ? true : orgDataCompleteV1;

    const lensFolderData = isMyLensesOrgsV2Enabled ? undefined : lensFolderDataV1;
    const lensFolderDataComplete = isMyLensesOrgsV2Enabled ? true : lensFolderDataCompleteV1;

    if (loading) {
        return <PageContentSpinner />;
    }

    if (error) {
        return <Navigate to={AbsoluteRoute.LENSES} replace />;
    }

    if (!data?.getLens.lens) {
        return <PageContentSpinner />;
    }

    if (!selectedLensFolder?.id && (!lensFolderDataComplete || !orgDataComplete)) {
        return <Navigate to={AbsoluteRoute.LENSES} replace />;
    }

    if (!selectedLensFolder?.id) {
        return (
            <Navigate
                replace
                to={generatePath(AbsoluteRoute.ORG_LENS_FOLDER_LENS, {
                    orgId: isMyLensesOrgsV2Enabled ? data.getLens.lens.organizationId : orgData?.id ?? "",
                    lensFolderId: isMyLensesOrgsV2Enabled
                        ? data.getLens.lens.lensAccountId?.lensSpaceId ?? ""
                        : lensFolderData?.id ?? "",
                    lensId,
                })}
            />
        );
    }

    return <LensSourcePage {...data.getLens.lens} />;
};
